import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import FilterForPlanAction from 'src/pages/DashboardPage/vpo/components/planAction/filterForPlanAction/FilterForPlanAction';
import ReportActions from './ReportActions';
import { fetchData } from '../../actions/actions';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectModelData } from '../../selectors/selectors';
import Loading from 'src/components/Loading';
import { getDefaultReportData, processReportDataByQuarters, ReportAnswer, transformFiltersToApiFormat } from '../../utils/reportActions';
import { getFactoriesForSelectedGroups, getGOPsForSelectedGroups, transformDataForFilters, transformDataForFiltersGOPs } from '../../utils/reports';
import { debounce, isEqual } from 'lodash';
import { fetchReportDataRequest } from '../../store/reports/actions';
import { selectReportAnswers } from '../../store/reports/selectors';
import { fetchLinkedActions } from '../../store/link-actions/actions';

export const getDirectionsForSelectedGOPs = (currentFilters: any, gopDirections: any[]): any[] => {
  if (!currentFilters?.formGop?.selectedIds?.length) {
    return gopDirections;
  }
  const selectedGopIds = currentFilters.formGop.selectedIds;
  const filteredDirections = gopDirections.filter((direction) => {
    if (direction.gop && Array.isArray(direction.gop)) {
      return direction.gop.some((gopItem: any) => selectedGopIds.includes(gopItem.id));
    }
    const directionGopId = direction.gop?.id || direction.gop || direction.gopId;
    if (directionGopId) {
      return selectedGopIds.includes(directionGopId);
    }
    return false;
  });
  return filteredDirections;
};

interface FilterState {
  selected: string[];
  selectedIds: number[];
  search: string;
}
interface CurrentFilters {
  year: FilterState;
  groupGop: FilterState;
  factoryGop: FilterState;
  formGop: FilterState;
  focusGop: FilterState;
}

export const years = generateYears(2025, new Date().getFullYear());

function generateYears(fromYear: number, toYear: number) {
  const years = [];
  for (let year = fromYear; year <= toYear; year++) {
    years.push({ ID: year - fromYear + 1, Title: String(year) });
  }
  return years;
}

export const filterConfigActionGop = {
  year: {
    title: 'Год',
    hasSearch: false,
    searchPlaceholder: 'Поиск по году',
    options: years.map((item) => item.Title),
  },
  groupGop: {
    title: 'Группа',
    hasSearch: true,
    searchPlaceholder: 'Поиск по группе',
    options: [],
  },
  factoryGop: {
    title: 'Завод',
    hasSearch: true,
    searchPlaceholder: 'Поиск по заводу',
    options: [],
  },
  formGop: {
    title: 'Форма GOP',
    hasSearch: true,
    searchPlaceholder: 'Поиск по форме GOP',
    options: [],
  },
  focusGop: {
    title: 'Фокусные GOP',
    hasSearch: false,
    searchPlaceholder: 'Поиск по фокусным GOP',
    options: [],
  },
};

export const filtersOpenActionGop = {
  year: false,
  groupGop: false,
  factoryGop: false,
  formGop: false,
  focusGop: false,
};

const focusGop = [
  { ID: 1, Title: 'Да', Value: true },
  { ID: 2, Title: 'Нет', Value: false },
];

const getCurrentDate = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  return {
    year: {
      id: currentYear,
      name: currentYear.toString(),
    },
  };
};

const getInitialFiltersWithDefaults = (groupGop: any[], formGops: any[]): CurrentFilters => {
  const currentDate = getCurrentDate();

  const sortedGroups = [...groupGop].sort((a, b) => {
    const titleA = (a.Title || a.title || '').toLowerCase();
    const titleB = (b.Title || b.title || '').toLowerCase();
    return titleA.localeCompare(titleB);
  });

  const sortedGops = [...formGops].sort((a, b) => {
    const titleA = (a.description || a.description || '').toLowerCase();
    const titleB = (b.description || b.description || '').toLowerCase();
    return titleA.localeCompare(titleB);
  });

  const firstGroup = sortedGroups[0];
  const firstGop = sortedGops[0];

  return {
    year: {
      selected: [currentDate.year.name],
      selectedIds: [currentDate.year.id],
      search: '',
    },
    groupGop: {
      selected: firstGroup ? [firstGroup.Title || firstGroup.title] : [],
      selectedIds: firstGroup ? [firstGroup.id] : [],
      search: '',
    },
    factoryGop: { selected: [], selectedIds: [], search: '' },
    formGop: {
      selected: firstGop ? [firstGop.description || firstGop.description] : [],
      selectedIds: firstGop ? [firstGop.id] : [],
      search: '',
    },
    focusGop: { selected: [], selectedIds: [], search: '' },
  };
};

export default function ReportActionsMain() {
  const [currentFilters, setCurrentFilters] = useState<CurrentFilters | null>(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [activeDirection, setActiveDirection] = useState<number | null>(null);
  const lastFetchParams = useRef<string>('');
  const isInitialDirectionSet = useRef(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const groupGop = useAppSelector((state: any) => selectModelData(state, 'gop-groups'));
  const formGops = useAppSelector((state: any) => selectModelData(state, 'gop-gops'));
  const gopDirection = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));
  const allAnswers = useAppSelector(selectReportAnswers) as ReportAnswer[];

  const filteredDirections = useMemo(() => {
    return getDirectionsForSelectedGOPs(currentFilters, gopDirection || []);
  }, [currentFilters, gopDirection]);

  const isFocal = useMemo(() => {
    if (!currentFilters?.focusGop?.selected?.length) {
      return undefined;
    }
    const selectedTitles = currentFilters.focusGop.selected;
    const hasYes = selectedTitles.includes('Да');
    const hasNo = selectedTitles.includes('Нет');
    if (hasYes && hasNo) {
      return undefined;
    }
    if (selectedTitles.length === 1) {
      const selectedTitle = selectedTitles[0];
      const focalItem = focusGop.find((focal) => focal.Title === selectedTitle);
      return focalItem?.Value;
    }
    return undefined;
  }, [currentFilters?.focusGop?.selected]);

  const listNames = ['gop-groups', 'gop-factories'];

  useEffect(() => {
    listNames.forEach((listName) => {
      dispatch(fetchData(listName));
    });
  }, [dispatch]);

  useEffect(() => {
    const selectedGopIds = currentFilters?.formGop?.selectedIds;
    dispatch(fetchData('gop-directions', { gop: selectedGopIds }));
  }, [dispatch, currentFilters?.formGop?.selectedIds]);

  useEffect(() => {
    const selectedGroupIds = currentFilters?.groupGop?.selectedIds;
    dispatch(
      fetchData(
        'gop-gops',
        {
          group: selectedGroupIds,
        },
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
  }, [dispatch, currentFilters?.groupGop?.selectedIds]);

  useEffect(() => {
    if (groupGop.length > 0 && formGops.length > 0 && !filtersInitialized) {
      const initialFilters = getInitialFiltersWithDefaults(groupGop, formGops);
      setCurrentFilters(initialFilters);
      setFiltersInitialized(true);
    }
  }, [groupGop, formGops, filtersInitialized]);

  useEffect(() => {
    if (filteredDirections && filteredDirections.length > 0) {
      const currentDirectionExists = activeDirection && filteredDirections.some((dir) => dir.id === activeDirection);

      if (!currentDirectionExists) {
        const firstDirection = filteredDirections[0];
        setActiveDirection(firstDirection.id);
        isInitialDirectionSet.current = true;
      }
    } else if (filteredDirections && filteredDirections.length === 0 && activeDirection !== null) {
      setActiveDirection(null);
    }
  }, [filteredDirections]);

  const availableGOPs = useMemo(() => getGOPsForSelectedGroups(currentFilters, groupGop), [currentFilters, groupGop]);
  const availableFactories = useMemo(() => getFactoriesForSelectedGroups(currentFilters, groupGop), [currentFilters, groupGop]);
  const transformedFactoriesGop = useMemo(() => transformDataForFilters(availableFactories), [availableFactories]);
  const transformedFormGops = useMemo(() => transformDataForFiltersGOPs(availableGOPs), [availableGOPs]);
  const transformedGroupGop = useMemo(() => transformDataForFilters(groupGop), [groupGop]);

  const fetchReportData = useCallback(() => {
    if (!currentFilters || !filtersInitialized || activeDirection === null) return;
    const apiFilters = transformFiltersToApiFormat(currentFilters, activeDirection, focusGop);
    const fetchParamsKey = JSON.stringify({
      filters: apiFilters,
      direction: activeDirection,
      focal: isFocal,
    });
    if (lastFetchParams.current === fetchParamsKey) {
      return;
    }
    lastFetchParams.current = fetchParamsKey;
    //! Получаю ответы
    dispatch(fetchReportDataRequest(apiFilters));
    //! Действия
    dispatch(fetchLinkedActions(apiFilters));
  }, [currentFilters, filtersInitialized, activeDirection, isFocal, dispatch]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const reportData = useMemo(() => {
    if (allAnswers && allAnswers.length > 0) {
      return processReportDataByQuarters(allAnswers);
    }
    return getDefaultReportData();
  }, [allAnswers]);

  const debouncedSetFilters = useMemo(
    () =>
      debounce((newFilters: any) => {
        setCurrentFilters(newFilters);
      }, 1000),
    []
  );

  const handleFiltersChange = useCallback(
    (newFilters: any) => {
      console.log('Новые фильтры:', newFilters);
      debouncedSetFilters(newFilters);
    },
    [debouncedSetFilters]
  );

  const handleDirectionChange = useCallback((directionId: number) => {
    setActiveDirection(directionId);
  }, []);

  const filterConfigs = useMemo(
    () => ({
      year: { items: years },
      groupGop: { items: transformedGroupGop },
      factoryGop: { items: transformedFactoriesGop },
      formGop: { items: transformedFormGops },
      focusGop: { items: focusGop },
    }),
    [transformedGroupGop, transformedFactoriesGop, transformedFormGops]
  );

  const filtersMap = useMemo(
    () => ({
      year: years,
      formGop: transformedFormGops,
      factoryGop: transformedFactoriesGop,
      groupGop: transformedGroupGop,
      focusGop: focusGop,
    }),
    [transformedFormGops, transformedFactoriesGop, transformedGroupGop]
  );

  const selectedFactoryIds = useMemo(() => {
    return currentFilters?.factoryGop?.selectedIds || [];
  }, [currentFilters]);

  if (!filtersInitialized || !currentFilters) {
    return (
      <div className='flex justify-between w-full h-full'>
        <div className='w-[320px] mr-2 bg-white'>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className='flex justify-between w-full h-full'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={currentFilters}
          filtersOpen={filtersOpenActionGop}
          filtersConfig={filterConfigActionGop}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'max-h-screen'}
        />
      </div>

      <div className='flex-1 min-w-0 m-6'>
        <div className='flex items-center text-gray-600 mb-6'>
          <span className='pr-3'>
            <ReportIcon />
          </span>
          <span className='cursor-pointer text-base' onClick={() => navigate('/dashboard/2/reports')}>
            Отчеты
          </span>
          <span className='mx-2'>→</span>
          <span className='text-base'>Отчет по выполненным действиям</span>
        </div>

        <div className='min-w-0'>
          <ReportActions
            data={reportData}
            activeDirection={activeDirection}
            onDirectionChange={handleDirectionChange}
            isFocal={isFocal}
            availableDirections={filteredDirections}
            selectedFactoryIds={selectedFactoryIds}
          />
        </div>
      </div>
    </div>
  );
}
